import { Container, Graphics, NineSliceSprite, Sprite, TilingSprite, type Texture, type Ticker } from 'pixi.js';
import { ease, prefersReducedMotion } from '../board/ui';

/** The art the attract reels are painted with: the first slot's own. */
export interface AttractArt {
  /** The slot's backdrop, cover-fitted to the screen. */
  background: Texture | null;
  /** The tiled reel column. */
  column: Texture | null;
  /** The carved reel frame (a nine-slice). */
  border: Texture | null;
  /** Reel symbols; the last few are the rarer ones that line up for a win. */
  symbols: readonly Texture[];
  coin: Texture | null;
}

const REELS = 5;
const ROWS = 3;
const SPIN_SPEED = 1500; // px a second at a 100px cell, scaled with the cell
const SPIN_MS = 1000;
const STAGGER_MS = 190;
const LAND_MS = 280;
const SHOW_WIN_MS = 1900;
const REST_MS = 900;

interface Column {
  box: Container;
  sprites: Sprite[];
  offset: number;
  spinning: boolean;
}

interface Coin {
  sprite: Sprite;
  vx: number;
  vy: number;
  spin: number;
}

/**
 * The game playing itself behind the age check (producer 2026-10-14:
 * "something that makes them want to get past it"): the first slot's
 * backdrop and reels, spinning, landing and now and then lining up a win
 * that throws coins. Purely for show, silent, and still for players who
 * ask for reduced motion.
 */
export class AttractReels extends Container {
  private readonly background = new Sprite();
  private readonly machine = new Container();
  private readonly frame: NineSliceSprite | null = null;
  private readonly columns: Column[] = [];
  private readonly dividers = new Graphics();
  private readonly glow = new Graphics();
  private readonly coins = new Container();
  private flying: Coin[] = [];
  private cell = 60;
  private gap = 5;
  private viewH = 800;
  private running = false;
  private cycle = 0;
  private timers: ReturnType<typeof setTimeout>[] = [];
  private winRow: Sprite[] = [];
  private winClock = 0;
  private readonly tick = (ticker: Ticker): void => this.update(ticker.deltaMS);

  constructor(
    private readonly art: AttractArt,
    private readonly ticker: Ticker,
  ) {
    super();
    this.eventMode = 'none';
    if (art.background) this.background.texture = art.background;
    this.background.anchor.set(0.5);
    this.addChild(this.background, this.machine, this.coins);
    for (let i = 0; i < REELS; i++) {
      const box = new Container();
      const mask = new Graphics();
      box.mask = mask;
      box.addChild(mask);
      const back = art.column ? new TilingSprite({ texture: art.column, width: 1, height: 1 }) : new Graphics();
      box.addChild(back);
      const sprites: Sprite[] = [];
      for (let r = 0; r <= ROWS; r++) {
        const s = new Sprite(this.randomSymbol());
        s.anchor.set(0.5);
        sprites.push(s);
        box.addChild(s);
      }
      this.machine.addChild(box);
      this.columns.push({ box, sprites, offset: 0, spinning: false });
    }
    this.machine.addChild(this.dividers, this.glow);
    if (art.border) {
      const inset = Math.round(Math.min(art.border.width, art.border.height) * (152 / 1254));
      this.frame = new NineSliceSprite({
        texture: art.border,
        leftWidth: inset,
        topHeight: inset,
        rightWidth: inset,
        bottomHeight: inset,
      });
      this.machine.addChild(this.frame);
    }
  }

  /** Height of the machine, frame included, at a given width. */
  static heightFor(width: number): number {
    const cell = AttractReels.cellFor(width);
    return Math.round(ROWS * cell + 2 * AttractReels.frameOut(cell));
  }

  private static cellFor(width: number): number {
    // Five cells, four gaps (8% of a cell) and the frame on both sides.
    return Math.max(24, width / (REELS + (REELS - 1) * 0.08 + 2 * 0.36));
  }

  /** How far the carved frame reaches outside the reels. */
  private static frameOut(cell: number): number {
    return Math.round(cell * 0.36);
  }

  /**
   * Cover the screen with the backdrop and fit the machine into the box
   * at (`x`, `y`), `width` wide.
   */
  layout(viewW: number, viewH: number, box: { x: number; y: number; width: number }): void {
    this.viewH = viewH;
    if (this.art.background) {
      const tex = this.art.background;
      const k = Math.max(viewW / tex.width, viewH / tex.height);
      this.background.scale.set(k);
      this.background.position.set(viewW / 2, viewH / 2);
    }
    const cell = AttractReels.cellFor(box.width);
    const out = AttractReels.frameOut(cell);
    this.cell = cell;
    this.gap = Math.round(cell * 0.08);
    const reelsW = REELS * cell + (REELS - 1) * this.gap;
    const reelsH = ROWS * cell;
    this.machine.position.set(Math.round(box.x + (box.width - reelsW) / 2), Math.round(box.y + out));
    this.columns.forEach((col, i) => {
      col.box.x = i * (cell + this.gap);
      const mask = col.box.mask as Graphics;
      mask.clear().rect(0, 0, cell, reelsH).fill({ color: 0xffffff });
      const back = col.box.children[1];
      if (back instanceof TilingSprite && this.art.column) {
        back.width = cell;
        back.height = reelsH;
        back.tileScale.set(cell / this.art.column.width);
      } else if (back instanceof Graphics) {
        back.clear().rect(0, 0, cell, reelsH).fill({ color: 0x1d2a1c });
      }
      for (const s of col.sprites) this.sizeSymbol(s);
      this.placeColumn(col);
    });
    this.dividers.clear();
    for (let i = 1; i < REELS; i++) {
      const x = i * (cell + this.gap) - this.gap / 2;
      this.dividers.rect(x - 1.5, 0, 3, reelsH);
    }
    this.dividers.fill({ color: 0x000000, alpha: 0.85 });
    if (this.frame && this.art.border) {
      const inset = this.frame.leftWidth;
      // The painted ring reaches a little over the outer reels, as the
      // slot's own frame does.
      const k = (out * 1.25) / inset;
      this.frame.scale.set(k);
      this.frame.width = (reelsW + 2 * out) / k;
      this.frame.height = (reelsH + 2 * out) / k;
      this.frame.position.set(-out, -out);
    }
    this.drawGlow(this.winClock);
  }

  /** Spin, land and now and then win, over and over. */
  start(): void {
    if (this.running) return;
    this.running = true;
    if (prefersReducedMotion()) return;
    this.ticker.add(this.tick);
    this.schedule(400, () => this.spin());
  }

  /** Show or hide the reels, leaving only the backdrop. */
  setMachineVisible(visible: boolean): void {
    this.machine.visible = visible;
    this.coins.visible = visible;
  }

  /** Stop where the reels are (the game is not for this player). */
  stop(): void {
    this.running = false;
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
    this.ticker.remove(this.tick);
    for (const col of this.columns) {
      col.spinning = false;
      col.offset = 0;
      this.placeColumn(col);
    }
    this.clearWin();
  }

  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    this.stop();
    super.destroy(options);
  }

  private schedule(ms: number, fn: () => void): void {
    const t = setTimeout(() => {
      this.timers = this.timers.filter((x) => x !== t);
      if (this.running) fn();
    }, ms);
    this.timers.push(t);
  }

  private spin(): void {
    this.clearWin();
    this.cycle += 1;
    // Every other spin lines up a row of one of the rarer symbols.
    const winner = this.cycle % 2 === 0 ? this.rareSymbol() : null;
    for (const col of this.columns) col.spinning = true;
    this.columns.forEach((col, i) => {
      this.schedule(SPIN_MS + i * STAGGER_MS, () => {
        const rows = [this.randomSymbol(), winner ?? this.randomSymbol(), this.randomSymbol()];
        if (!winner && i === REELS - 1) {
          // No accidental near-full line on a losing spin.
          const middle = this.columns.slice(0, REELS - 1).map((c) => c.sprites[2]!.texture);
          if (middle.every((t) => t === middle[0])) rows[1] = this.otherSymbol(middle[0]!);
        }
        this.land(col, rows);
      });
    });
    const landed = SPIN_MS + (REELS - 1) * STAGGER_MS + LAND_MS;
    if (winner) {
      this.schedule(landed, () => this.showWin());
      this.schedule(landed + SHOW_WIN_MS, () => this.spin());
    } else {
      this.schedule(landed + REST_MS, () => this.spin());
    }
  }

  private land(col: Column, rows: Texture[]): void {
    col.spinning = false;
    // Snap the target symbols in just above their rows, then drop them
    // into place with a little bounce.
    col.sprites[0]!.texture = this.randomSymbol();
    rows.forEach((t, r) => (col.sprites[r + 1]!.texture = t));
    for (const s of col.sprites) this.sizeSymbol(s);
    const from = -this.cell * 0.6;
    const start = performance.now();
    const step = (): void => {
      if (!this.running || col.spinning) return;
      const t = Math.min(1, (performance.now() - start) / LAND_MS);
      col.offset = from * (1 - ease.outBack(t));
      this.placeColumn(col);
      if (t < 1) requestAnimationFrame(step);
    };
    step();
  }

  private showWin(): void {
    this.winRow = this.columns.map((c) => c.sprites[2]!);
    this.winClock = 0;
    this.burstCoins();
  }

  private clearWin(): void {
    for (const s of this.winRow) this.sizeSymbol(s);
    this.winRow = [];
    this.glow.clear();
  }

  private update(deltaMs: number): void {
    const speed = (SPIN_SPEED * this.cell) / 100;
    for (const col of this.columns) {
      if (!col.spinning) continue;
      col.offset += (speed * deltaMs) / 1000;
      while (col.offset >= this.cell) {
        col.offset -= this.cell;
        // The bottom symbol leaves; a fresh one comes in at the top.
        const last = col.sprites.pop()!;
        last.texture = this.randomSymbol();
        this.sizeSymbol(last);
        col.sprites.unshift(last);
      }
      this.placeColumn(col);
    }
    if (this.winRow.length > 0) {
      this.winClock += deltaMs;
      const pulse = 1 + 0.1 * Math.sin((this.winClock / 1000) * Math.PI * 3);
      for (const s of this.winRow) this.sizeSymbol(s, pulse);
      this.drawGlow(this.winClock);
    }
    this.updateCoins(deltaMs);
  }

  private placeColumn(col: Column): void {
    col.sprites.forEach((s, i) => {
      s.position.set(this.cell / 2, (i - 1) * this.cell + this.cell / 2 + col.offset);
    });
  }

  private sizeSymbol(s: Sprite, scale = 1): void {
    const size = this.cell * 0.9 * scale;
    const tex = s.texture;
    const k = size / Math.max(1, tex.width, tex.height);
    s.scale.set(k);
  }

  private drawGlow(clock: number): void {
    this.glow.clear();
    if (this.winRow.length === 0) return;
    const reelsW = REELS * this.cell + (REELS - 1) * this.gap;
    const y = this.cell * 1.5;
    const alpha = 0.55 + 0.35 * Math.sin((clock / 1000) * Math.PI * 3);
    this.glow
      .roundRect(-4, y - this.cell / 2, reelsW + 8, this.cell, 10)
      .stroke({ color: 0xffe48a, width: 4, alpha })
      .roundRect(-4, y - this.cell / 2, reelsW + 8, this.cell, 10)
      .fill({ color: 0xffd36b, alpha: alpha * 0.12 });
  }

  private burstCoins(): void {
    if (!this.art.coin) return;
    const reelsW = REELS * this.cell + (REELS - 1) * this.gap;
    const originX = this.machine.x + reelsW / 2;
    const originY = this.machine.y + this.cell * 1.5;
    for (let i = 0; i < 22; i++) {
      const sprite = new Sprite(this.art.coin);
      sprite.anchor.set(0.5);
      const size = this.cell * (0.32 + Math.random() * 0.18);
      sprite.scale.set(size / this.art.coin.width);
      sprite.position.set(originX + (Math.random() - 0.5) * reelsW * 0.7, originY);
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.6;
      const power = this.cell * (7 + Math.random() * 6);
      this.coins.addChild(sprite);
      this.flying.push({
        sprite,
        vx: Math.cos(angle) * power,
        vy: Math.sin(angle) * power,
        spin: (Math.random() - 0.5) * 8,
      });
    }
  }

  private updateCoins(deltaMs: number): void {
    if (this.flying.length === 0) return;
    const dt = deltaMs / 1000;
    const gravity = this.cell * 22;
    const floor = this.viewH + this.cell;
    this.flying = this.flying.filter((c) => {
      c.vy += gravity * dt;
      c.sprite.x += c.vx * dt;
      c.sprite.y += c.vy * dt;
      c.sprite.rotation += c.spin * dt;
      if (c.sprite.y > floor) {
        c.sprite.destroy();
        return false;
      }
      return true;
    });
  }

  private randomSymbol(): Texture {
    const list = this.art.symbols;
    return list[Math.floor(Math.random() * list.length)]!;
  }

  private rareSymbol(): Texture {
    const list = this.art.symbols;
    const rare = list.slice(Math.max(0, list.length - 3));
    return rare[Math.floor(Math.random() * rare.length)]!;
  }

  private otherSymbol(not: Texture): Texture {
    const others = this.art.symbols.filter((t) => t !== not);
    return others[Math.floor(Math.random() * others.length)] ?? not;
  }
}
